import { AlertDialogFooter } from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAudio } from "@/contexts/AudioContext"
import { KeyboardIcon, SparklesIcon } from "lucide-react"
import React from "react"

export default function ShortcutsDialog({ open, setOpen }) {
    const { audioSettings, setAudioSettings } = useAudio()
    const [editingKey, setEditingKey] = React.useState(null)

    const handleEscapeKey = (event) => {
        if (event.key === "Escape") {
            // If editingKey is not null, prevent the modal from closing
            if (editingKey !== null) {
                event.preventDefault();
            } else {
                setOpen(false); // Close the modal if editingKey is null
            }
        }
    };

    const handleKeyDown = (event) => {
        // Check if it's a modifier key alone (Ctrl, Alt, Shift)
        if (event.ctrlKey || event.altKey || event.shiftKey) {
            // If only a modifier key is pressed, don't do anything yet
            if (!event.key || event.key === 'Control' || event.key === 'Alt' || event.key === 'Shift') {
                return;
            }
        }

        let keyCombination = "";

        // Add modifier keys to the combination
        if (event.ctrlKey) keyCombination += "Ctrl+";
        if (event.altKey) keyCombination += "Alt+";
        if (event.shiftKey) keyCombination += "Shift+";

        // Add the main key
        keyCombination += event.key;

        const key = event.key.toLowerCase()

        // Prevent default behavior for certain browser shortcuts (like Ctrl+P, Ctrl+1 for tab switching)
        if (
            // Browser shortcuts that should be prevented
            (event.ctrlKey && key === "p") ||  // Ctrl + P -> Print
            (event.ctrlKey && key === "&") ||  // Ctrl + P -> Print
            (event.ctrlKey && key === "é") ||  // Ctrl + P -> Print
            (event.ctrlKey && key === '"') ||  // Ctrl + P -> Print
            (event.ctrlKey && key === "'") ||  // Ctrl + P -> Print
            (event.ctrlKey && key === "(") ||  // Ctrl + P -> Print
            (event.ctrlKey && key === "-") ||  // Ctrl + P -> Print
            (event.ctrlKey && key === "è") ||  // Ctrl + P -> Print
            (event.ctrlKey && key === "_") ||  // Ctrl + P -> Print
            (event.ctrlKey && key === "ç") ||  // Ctrl + P -> Print
            (event.ctrlKey && key === "à") ||  // Ctrl + P -> Print
            (event.ctrlKey && key === "t") ||  // Ctrl + T -> Open new tab
            (event.ctrlKey && key === "w") ||  // Ctrl + W -> Close tab
            (event.ctrlKey && key === "r") ||  // Ctrl + R -> Reload page
            (event.ctrlKey && key === "f") ||  // Ctrl + F -> Find on page
            (event.ctrlKey && key === "a") ||  // Ctrl + A -> Select all
            (event.ctrlKey && key === "l") ||  // Ctrl + A -> Select all
            (event.ctrlKey && key === "1") ||  // Ctrl + 1 -> Switch to first tab
            (event.ctrlKey && key === "2") ||  // Ctrl + 2 -> Switch to second tab
            (event.ctrlKey && key === "3") ||  // Ctrl + 3 -> Switch to third tab
            (event.ctrlKey && key === "4") ||  // Ctrl + 4 -> Switch to fourth tab
            (event.ctrlKey && key === "5") ||  // Ctrl + 5 -> Switch to fifth tab
            (event.ctrlKey && key === "6") ||  // Ctrl + 6 -> Switch to sixth tab
            (event.ctrlKey && key === "7") ||  // Ctrl + 7 -> Switch to seventh tab
            (event.ctrlKey && key === "8") ||  // Ctrl + 8 -> Switch to eighth tab
            (event.ctrlKey && key === "9") ||  // Ctrl + 9 -> Switch to last tab
            (event.ctrlKey && key === "k") ||  // Ctrl + 9 -> Switch to last tab
            (event.ctrlKey && key === "l") ||  // Ctrl + 9 -> Switch to last tab
            (key === "f1") || // Ctrl + F12 -> Open Developer Tools
            (key === "f2") || // Ctrl + F12 -> Open Developer Tools
            (key === "f3") || // Ctrl + F12 -> Open Developer Tools
            (key === "f4") || // Ctrl + F12 -> Open Developer Tools
            (key === "f5") || // Ctrl + F12 -> Open Developer Tools
            (key === "f6") || // Ctrl + F12 -> Open Developer Tools
            (key === "f7") || // Ctrl + F12 -> Open Developer Tools
            (key === "f8") || // Ctrl + F12 -> Open Developer Tools
            (key === "f9") || // Ctrl + F12 -> Open Developer Tools
            (key === "f10") || // Ctrl + F12 -> Open Developer Tools
            (key === "f11") || // Ctrl + F12 -> Open Developer Tools
            (key === "f12")
        ) {
            event.preventDefault();
        }

        // Update the audioSettings with the new key combination
        if (editingKey) {
            const updatedShortcuts = { ...audioSettings.shortcuts };
            updatedShortcuts[editingKey].key = keyCombination;
            updatedShortcuts[editingKey].labelKey = convertKeyString(keyCombination);

            // Save the updated settings
            setAudioSettings((prevSettings) => ({
                ...prevSettings,
                shortcuts: updatedShortcuts,
            }));

            // Stop editing mode
            setEditingKey(null);
        }

        // Prevent propagation of the event to avoid unwanted side effects
        event.stopPropagation();
    };


    React.useEffect(() => {
        if (editingKey) {
            const handleKeyDownGlobal = (event) => {
                handleKeyDown(event)
            }

            window.addEventListener("keydown", handleKeyDownGlobal)

            return () => {
                window.removeEventListener("keydown", handleKeyDownGlobal)
            }
        }
    }, [editingKey])

    return <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} onEscapeKeyDown={(event) => { if (editingKey) event.preventDefault() }}>
            <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><KeyboardIcon /> Raccourcies clavier</DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2">
                {Object.keys(audioSettings.shortcuts).map((key) => (
                    <React.Fragment key={key}>
                        <Label className="self-center whitespace-nowrap">
                            <div className="flex items-center gap-2">{key === 'assistant' && <SparklesIcon className="w-4 h-4" strokeWidth={1} />} {audioSettings.shortcuts[key].label}</div>
                        </Label>

                        <div className="flex items-center gap-2">
                            <Input
                                className="lowercase"
                                autoFocus={false}
                                value={editingKey === key ? "En attente d'un raccourci..." : audioSettings.shortcuts[key].labelKey}
                                onMouseDown={() => setEditingKey(key)}
                                readOnly
                            />
                            {editingKey === key && (
                                <Button
                                    variant="outline"
                                    onClick={() => setEditingKey(null)}
                                >
                                    Annuler
                                </Button>
                            )}
                            {editingKey !== key && (
                                <Button
                                    variant="outline"
                                    onClick={() => setEditingKey(key)}
                                >
                                    Définir
                                </Button>
                            )}
                        </div>
                    </React.Fragment>
                ))}
                <Label className="self-center whitespace-nowrap">
                    Intervalle de saut (sec)
                </Label>

                <div className="flex items-center gap-2">
                    <Input
                        type="number"
                        value={audioSettings.timeJump}
                        onChange={(e) => setAudioSettings({
                            ...audioSettings,
                            timeJump: parseInt(e.target.value)
                        })}
                    />
                </div>


            </div>
        </DialogContent>
    </Dialog>
}

function azertyLayoutToNumber(key) {
    switch (key) {
        case '&':
            return "1"
        case 'é':
            return "2"
        case '"':
            return "3"
        case "'":
            return "4"
        case '(':
            return "5"
        case '-':
            return "6"
        case 'è':
            return "7"
        case '_':
            return "8"
        case 'ç':
            return "9"
        case 'à':
            return "0"
        default:
            return key
    }
}

export function convertKeyString(keyString) {
    return keyString.split('+').map(key => azertyLayoutToNumber(key)).join('+');
}